import { useEffect, useRef, useCallback, useMemo } from "react";
import * as Sentry from "../lib/errorReporting";
import { supabase } from "../lib/supabase";
import { App } from "@capacitor/app";
import { useRealtimeChannel } from "./useRealtimeChannel";
import { chunk, BETS_SEED_CHUNK } from "../lib/bet-load";

/**
 * Patch bets state with settlement fields from a Realtime UPDATE payload.
 * A present key overwrites (DB authoritative even when null); an absent key
 * means unchanged (#1544: under REPLICA IDENTITY DEFAULT an UPDATE that leaves
 * a TOASTed value unchanged arrives in payload.new without that column).
 * Returns reference-identical array when nothing changed.
 */
function applySettlementPatch(prevBets, row) {
  const id = String(row.id);
  let changed = false;
  const result = prevBets.map((bet) => {
    if (String(bet.id) !== id) return bet;
    // Only patch if values actually differ
    const newFinalResult = "final_result" in row ? (row.final_result ?? null) : (bet.finalResult ?? null);
    const newSettledAt = "settled_at" in row
      ? (row.settled_at ? new Date(row.settled_at).getTime() : null)
      : (bet.settledAt ?? null);
    const newAutoSettled = "auto_settled" in row ? (row.auto_settled ?? null) : (bet.autoSettled ?? null);
    if (
      (bet.finalResult ?? null) === newFinalResult &&
      (bet.settledAt ?? null) === newSettledAt &&
      (bet.autoSettled ?? null) === newAutoSettled
    ) {
      return bet;
    }
    changed = true;
    return {
      ...bet,
      finalResult: newFinalResult,
      settledAt: newSettledAt,
      autoSettled: newAutoSettled,
    };
  });
  return changed ? result : prevBets;
}

/**
 * Subscribe to Supabase bets Realtime UPDATE events and seed on mount.
 * When the settlement cron writes final_result + settled_at, this hook
 * patches the in-memory bet so it moves from Active to Results without reload.
 *
 * @param {Object} params
 * @param {Object|null} params.user - current user (null = guest, skip)
 * @param {Array} params.bets - current bets array
 * @param {Function} params.setBets - state setter for bets
 */
export function useBetsRealtime({ user, bets, setBets }) {
  const betsRef = useRef(bets);
  betsRef.current = bets;
  const userRef = useRef(user);
  userRef.current = user;

  // Stable serialized key of unsettled bet IDs — drives seed re-run
  const unsettledKey = useMemo(
    () =>
      bets
        .filter((b) => !b.settledAt)
        .map((b) => String(b.id))
        .sort()
        .join(","),
    [bets]
  );

  const seed = useCallback(async () => {
    if (!supabase || !userRef.current) return;
    const unsettledIds = betsRef.current
      .filter((b) => !b.settledAt)
      .map((b) => String(b.id));
    if (!unsettledIds.length) return;

    try {
      // #1554: chunked IN lists (<= 200 ids, ~4 KB) so the URL never nears the gateway's ~25 KB
      // limit. Chunks run in turn; rows are merged and applied once; any failure applies nothing.
      const rows = [];
      for (const ids of chunk(unsettledIds, BETS_SEED_CHUNK)) {
        const { data, error } = await supabase
          .from("bets")
          .select("id, final_result, settled_at, auto_settled")
          .in("id", ids)
          .not("final_result", "is", null);

        if (error) {
          const err = new Error(error.message || "bets seed query failed");
          err.code = error.code;
          err.details = error.details;
          err.hint = error.hint;
          throw err;
        }
        // A non-array answer is a failed chunk, never an empty one: apply nothing (#1554 pre-Codex F1).
        if (!Array.isArray(data)) throw new Error("bets seed chunk: data is not an array");
        rows.push(...data);
      }
      if (!rows.length) return;

      setBets((prev) => {
        let patched = prev;
        for (const row of rows) {
          patched = applySettlementPatch(patched, row);
        }
        return patched;
      });
    } catch (err) {
      // Recoverable: the seed re-runs on the next foreground, channel recovery or bets change
      // (#1554 Q6, CC-IMPL-037).
      Sentry.captureMessage("useBetsRealtime seed failed", {
        level: "warning",
        tags: { function: "useBetsRealtime", stage: "seed" },
        extra: { message: err?.message, code: err?.code },
      });
    }
  }, [setBets]);

  // Re-seed when the unsettled bet ID set changes
  useEffect(() => {
    if (!supabase || !user || !unsettledKey) return;
    seed();
  }, [user, unsettledKey, seed]);

  // buildChannel: attaches postgres_changes handler. Must be stable (useCallback).
  const buildChannel = useCallback(
    (channel) =>
      channel.on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "bets" },
        (payload) => {
          try {
            // #1544: Realtime answers a row it cannot deliver whole (401, 413 payload too large: only columns of
            // 64 bytes or less) with an `errors` stub -- re-seed rather than patch from it. Anything but a plain
            // object record is ignored (the `in` checks in the patch would throw inside the state updater).
            if (Array.isArray(payload.errors) && payload.errors.length > 0) {
              seed();
              return;
            }
            const row = payload.new;
            if (!row || typeof row !== "object" || Array.isArray(row)) return;
            setBets((prev) => applySettlementPatch(prev, row));
          } catch (err) {
            Sentry.captureException(err, {
              tags: { function: "useBetsRealtime", stage: "realtime-event" },
            });
          }
        }
      ),
    [setBets, seed]
  );

  // Re-seed on recovery to backfill any settlement writes missed during the outage.
  const onRecover = useCallback(() => {
    seed();
  }, [seed]);

  useRealtimeChannel({
    enabled: !!user,
    channelName: "bets-settlement",
    sentryContext: { function: "useBetsRealtime", channel: "bets-settlement" },
    buildChannel,
    onRecover,
  });

  // Capacitor foreground resume — re-seed to catch updates missed while backgrounded
  useEffect(() => {
    if (!user) return;
    let listenerHandle = null;
    let cancelled = false;
    (async () => {
      try {
        const handle = await App.addListener("appStateChange", ({ isActive }) => {
          if (isActive) seed();
        });
        if (cancelled) {
          handle.remove();
          return;
        }
        listenerHandle = handle;
      } catch (err) {
        Sentry.captureException(err, {
          tags: { function: "useBetsRealtime", stage: "capacitor-listener" },
        });
      }
    })();

    return () => {
      cancelled = true;
      if (listenerHandle) listenerHandle.remove();
    };
  }, [user, seed]);
}
